/**
 * wishlist-quick-buy.js
 * =====================
 * Provides full quick-buy modal functionality for the wishlist tab on
 * the customer account page.  Designed to produce behaviour identical
 * to the "Shop the Look" quick-buy on the product page.
 *
 * Dependencies (loaded BEFORE this file):
 *   • theme.js           — global layout, provides QuickBuyModal custom element
 *   • sibling-ajax.js    — size-gate, mutation observer, variant-picker UX
 *
 * Configuration (set inline in main-customers-account.liquid):
 *   window.__wishlistConfig = {
 *     colorSchemeClass : 'color-scheme color-scheme--<id>',
 *     sectionId        : '<main-product section id>'   // optional, for perf
 *   }
 *
 * Architecture notes:
 *   1. Modal sibling handler — lets users switch colour inside the modal
 *      (equivalent of the <script> in product-quick-buy.liquid).
 *   2. Wishlist renderer     — fetches Swym data, builds cards, creates
 *      <quick-buy-modal> elements, wires everything with aria-controls
 *      so the theme's native DialogElement delegate handles open/close.
 *   3. Single delegated click listener for the "REMOVE" action.
 *   4. Everything is wrapped in an IIFE — zero globals besides the config.
 */

(function () {
  'use strict';

  // =========================================================================
  //  0. CONFIGURATION
  // =========================================================================

  var cfg = window.__wishlistConfig || {};
  var COLOR_SCHEME = cfg.colorSchemeClass || 'color-scheme';

  // =========================================================================
  //  1. MODAL SIBLING HANDLER — R6
  //     Extracted to assets/quick-buy-sibling-handler.js.
  //     Loaded by main-product.liquid (PDP) and main-customers-account.liquid
  //     (account page). The __qbSiblingHandlerReady guard means whichever
  //     file is parsed first wins; the other is a no-op.
  // =========================================================================

  // =========================================================================
  //  2. UTILITY HELPERS
  // =========================================================================

  /** Convert any URL to a RELATIVE path, **stripping query params**.
   *  Swym often stores full URLs with ?variant=… which would pre-select
   *  a variant and prevent the size gate from working.                 */
  function toProductPath(url) {
    if (!url) return '';
    try {
      var parsed = new URL(url, window.location.origin);
      return parsed.pathname;                       // pathname only — no search
    } catch (e) {
      // Fallback: strip everything after '?'
      return url.split('?')[0];
    }
  }

  /** Shopify CDN image → 600px wide (or as supplied). */
  function optimizeImageUrl(src) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (src.indexOf('cdn.shopify.com') > -1) {
      src = src.replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)\./,
        '.'
      );
      src = src.replace(/(\.\w+)(\?|$)/, '_600x$1$2');
    }
    return src;
  }

  /** Extract Shopify product handle from any URL or path.
   *  e.g. "https://ossah.com/products/foo-bar?variant=1" → "foo-bar"
   *       "/products/foo-bar" → "foo-bar"                             */
  function extractHandle(url) {
    if (!url) return '';
    var m = url.match(/\/products\/([^?#\/]+)/);
    return m ? m[1] : '';
  }

  /** Shopify-style money formatter (USD). */
  /** Format a price using the shop's money format from __wishlistConfig. Falls back to USD. */
  function formatMoney(price) {
    if (!price && price !== 0) return '';
    var amount = parseFloat(price);
    if (isNaN(amount)) return '';
    var fmt = (cfg.moneyFormat || '').trim();
    if (fmt.indexOf('{{') !== -1) {
      var n = fmt.indexOf('no_decimals') !== -1 ? Math.round(amount).toString() : fmt.indexOf('comma_separator') !== -1 ? amount.toFixed(2).replace('.', ',') : amount.toFixed(2).replace(/.00$/, '');
      return fmt.replace(/{{s*amount[^}]*}}/g, n);
    }
    return '$' + amount.toFixed(2).replace(/.00$/, '');
  }


  // =========================================================================
  //  3. MODAL MANAGEMENT
  //     Creates <quick-buy-modal> elements identical to those produced by
  //     shop-the-look-card.liquid (same tag, attributes, classes).
  // =========================================================================

  var modalCache = {};

  /**
   * Create (or return cached) quick-buy modal for a wishlist product.
   * Returns the modal's DOM id string, or null if QuickBuyModal is
   * not registered.
   */
  function ensureModal(productId, productUrl) {
    var id = 'wishlist-qb-' + productId;
    if (modalCache[id]) return id;

    var Cls = customElements.get('quick-buy-modal');
    if (!Cls) {
      console.warn('[wishlist-quick-buy] quick-buy-modal custom element not registered');
      return null;
    }

    var cleanUrl = toProductPath(productUrl);

    var el = new Cls();
    el.id                  = id;
    el.setAttribute('product-url', cleanUrl);
    el.setAttribute('data-original-url', cleanUrl);   // remember for reset on close
    el.className           = 'modal modal--quick-buy ' + COLOR_SCHEME;

    // On close → reset product-url to the original so the next open
    // fetches the original colour/variant, not whatever the user
    // switched to during the session.
    el.addEventListener('dialog:after-hide', function () {
      var orig = this.getAttribute('data-original-url');
      if (orig) {
        this.setAttribute('product-url', orig);
      }
      this.removeAttribute('data-size-confirmed');
    });

    // Append to body — connectedCallback fires, theme's Delegate is set up
    document.body.appendChild(el);
    modalCache[id] = true;
    return id;
  }


  // =========================================================================
  //  4. WISHLIST CARD BUILDER
  //     Produces cards that mirror the Shop the Look layout.
  //     Button uses aria-controls so the THEME's own DialogElement delegate
  //     handles open → QuickBuyModal.show() runs the same code path.
  // =========================================================================

  /**
   * Build a single wishlist card DOM element.
   *
   * @param {Object} item  — Swym wishlist item (prices refreshed by fetchLivePrices)
   *   item.iu   — image URL
   *   item.pi   — product image (fallback)
   *   item.du   — detail URL
   *   item.dt   — display title
   *   item.pr   — price in DOLLARS (refreshed from Shopify; Swym cached value as fallback)
   *   item.empi — external master product ID
   *   item.epi  — external product (variant) ID
   * @param {number} idx — index for fallback ID
   */
  function buildCard(item, idx) {
    var img   = optimizeImageUrl(item.iu || item.pi || '');
    var url   = toProductPath(item.du || '');
    /* Display title — strip the size suffix Swym appends.
     *
     * Swym's API requires a variant ID on save, and Swym stores the variant
     * title as "{product title} - {color} : {size}" (e.g.
     * "Cloud Legging 25" - Black : XXS"). The size is included even when
     * the customer didn't explicitly choose it (the bookmark button defaults
     * to the first available variant).
     *
     * Premium UX: we save the variant internally (Swym needs it; useful
     * for Klaviyo abandonment-flow data later) but DISPLAY only the product
     * + colour portion so the customer doesn't see a phantom size they
     * didn't choose. The quick-buy modal opens with no size pre-selected
     * (toProductPath strips ?variant= from the URL), so the customer is
     * prompted to pick deliberately.
     *
     * Split on " : " (space-colon-space) to preserve any ":" that may
     * appear in colour names or product titles. */
    var rawTitle = item.dt || 'Product';
    var title    = rawTitle.split(' : ')[0].replace(/"/g, '&quot;');
    var price = formatMoney(item.pr);
    var pid   = item.empi || idx;
    var vid   = item.epi  || '';
    var mid   = ensureModal(pid, item.du || '');

    var div       = document.createElement('div');
    div.className = 'customer-dashboard__wishlist-card';
    div.dataset.pid = pid;
    div.dataset.vid = vid;

    // Build inner HTML ──────────────────────────────────────────────
    // The ADD TO BAG button uses aria-controls="{modal id}" so the
    // theme's native delegate in DialogElement handles the click and
    // calls QuickBuyModal.show() — identical to Shop the Look.
    //
    // <span> wrapper used instead of <button-content> custom element
    // to avoid constructor errors when built via innerHTML (the
    // ButtonContent constructor expects this.parentElement which is
    // null during innerHTML parsing → white/invisible text bug).

    var atcAttrs = mid
      ? 'aria-controls="' + mid + '"'
      : 'disabled';

    div.innerHTML =
      '<a href="' + url + '" class="customer-dashboard__wishlist-card-image">' +
        '<img src="' + img + '" alt="' + title + '" loading="lazy">' +
      '</a>' +
      '<div class="customer-dashboard__wishlist-card-info">' +
        '<a href="' + url + '" class="customer-dashboard__wishlist-card-title">' + title + '</a>' +
        '<span class="customer-dashboard__wishlist-card-price">' + price + '</span>' +
      '</div>' +
      '<div class="customer-dashboard__wishlist-card-actions">' +
        '<button type="button" class="customer-dashboard__wishlist-atc" ' + atcAttrs + '>' +
          '<span>ADD TO BAG</span>' +
        '</button>' +
        '<button type="button" class="customer-dashboard__wishlist-remove" data-action="remove">' +
          'REMOVE' +
        '</button>' +
      '</div>';

    return div;
  }


  // =========================================================================
  //  5. WISHLIST STATE & RENDERER
  // =========================================================================

  var wl = { loaded: false, grid: null, container: null, empty: null, count: null };

  function refreshCount() {
    if (!wl.grid) return;
    var n = wl.grid.children.length;
    if (n === 0) {
      wl.container.style.display = 'none';
      wl.empty.style.display = '';
    } else if (wl.count) {
      wl.count.textContent = n + ' item' + (n > 1 ? 's' : '');
    }
  }

  function renderWishlist(items) {
    wl.loaded = true;

    if (!items || items.length === 0) {
      wl.container.style.display = 'none';
      wl.empty.style.display     = '';
      return;
    }

    wl.empty.style.display = 'none';
    wl.container.innerHTML = '';

    // Item count
    wl.count = document.createElement('p');
    wl.count.className   = 'customer-dashboard__count';
    wl.count.textContent = items.length + ' item' + (items.length > 1 ? 's' : '');
    wl.container.appendChild(wl.count);

    // Card grid
    wl.grid = document.createElement('div');
    wl.grid.className = 'customer-dashboard__wishlist-grid';
    wl.container.appendChild(wl.grid);

    items.forEach(function (item, i) {
      wl.grid.appendChild(buildCard(item, i));
    });
  }

  // -------------------------------------------------------------------------
  //  5b. LIVE PRICE REFRESH
  //      Swym stores price at add-time and never updates it.  Products
  //      wishlisted during catalog setup may have $0 / placeholder prices.
  //      We fetch the CURRENT price from Shopify's AJAX product endpoint
  //      (/products/{handle}.js) before rendering cards.
  //
  //      • Deduplicates by handle — one fetch per product, not per item.
  //      • Matches variant-specific price via item.epi when possible.
  //      • Falls back gracefully to Swym's cached price on fetch error.
  //      • /products/{handle}.js prices are in CENTS; we divide by 100.
  // -------------------------------------------------------------------------

  /**
   * Batch-fetch current prices from Shopify and overwrite stale Swym values.
   *
   * @param {Array}    items    — Swym wishlist items (mutated in place)
   * @param {Function} callback — called with the same items array once done
   */
  function fetchLivePrices(items, callback) {
    // Map each unique handle to the item indices that reference it
    var handleMap = {};   // { handle: [itemIndex, …] }
    var handles   = [];

    items.forEach(function (item, i) {
      var h = extractHandle(item.du);
      if (!h) return;
      if (!handleMap[h]) { handleMap[h] = []; handles.push(h); }
      handleMap[h].push(i);
    });

    if (handles.length === 0) { callback(items); return; }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    Promise.all(
      handles.map(function (h) {
        return fetch(root + 'products/' + h + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function ()  { return null; });           // network / 404 → keep Swym price
      })
    ).then(function (products) {
      products.forEach(function (product, i) {
        if (!product) return;                              // fetch failed — skip
        handleMap[handles[i]].forEach(function (idx) {
          var item  = items[idx];
          var price = product.price;                       // cents (product-level min)

          // Prefer the variant-specific price when we can match epi → variant.id
          if (item.epi && product.variants) {
            for (var v = 0; v < product.variants.length; v++) {
              if (product.variants[v].id === item.epi) {
                price = product.variants[v].price;
                break;
              }
            }
          }

          item.pr = price / 100;                           // cents → dollars
        });
      });
      callback(items);
    });
  }

  function loadWishlist() {
    if (typeof window._swat === 'undefined') {
      setTimeout(loadWishlist, 500);
      return;
    }

    wl.container = document.getElementById('swym-wishlist-container');
    wl.empty     = document.getElementById('swym-wishlist-empty');

    if (!wl.container || !wl.empty) return;

    window._swat.fetch(function (items) {
      if (!items || items.length === 0) {
        renderWishlist(items);
        return;
      }
      fetchLivePrices(items, function (refreshed) {
        renderWishlist(refreshed);
      });
    });
  }

  // Expose for tab-switching code
  window.__wishlistLoader = {
    load: loadWishlist,
    isLoaded: function () { return wl.loaded; }
  };


  // =========================================================================
  //  6. REMOVE HANDLER (single delegated listener)
  //     Only "remove" needs a custom handler.  ADD TO BAG is handled
  //     entirely by the theme's DialogElement delegate via aria-controls.
  // =========================================================================

  document.body.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-action="remove"]');
    if (!btn) return;

    var card = btn.closest('.customer-dashboard__wishlist-card');
    if (!card) return;

    e.preventDefault();
    e.stopPropagation();

    var pid = parseInt(card.dataset.pid, 10);
    var vid = parseInt(card.dataset.vid, 10);

    if (!window._swat) return;

    window._swat.removeFromWishList(
      { empi: pid, epi: vid },
      function () {
        // Animate out
        card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        card.style.opacity    = '0';
        card.style.transform  = 'scale(0.95)';

        setTimeout(function () {
          card.remove();

          // Clean up the associated modal
          var modalId = 'wishlist-qb-' + pid;
          var modalEl = document.getElementById(modalId);
          if (modalEl) modalEl.remove();
          delete modalCache[modalId];

          refreshCount();
        }, 300);
      }
    );
  }, true);   // capture phase — consistent with sibling-ajax.js

})();